import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { WizardLayout } from "@/components/we360/WizardLayout";
import DemoFormOverlay from "@/components/DemoFormOverlay";
import { useFormValidation } from "@/hooks/useFormValidation";
import { CheckCircle } from "lucide-react";
import { syncF5 } from "@/services/backendSync";
import Step1Anxiety from "./Step1Anxiety";
import Step2Stress from "./Step2Stress";
import Step3Wellbeing from "./Step3Wellbeing";
import Step4Comments from "./Step4Comments";
import Step5Summary from "./Step5Summary";
import {
  ANXIETY_OPTIONS, STRESS_OPTIONS, WELLBEING_OPTIONS,
  sumScores, calculateGlobalScore,
  classifyAnxiety, classifyStress, classifyWellbeing, classifyGlobal,
} from "./helpers";

const STEPS = [
  { id: "anxiety", title: "Ansiedad", sub: "GAD-5 adaptado" },
  { id: "stress", title: "Estrés y Agotamiento", sub: "Burnout" },
  { id: "wellbeing", title: "Bienestar Interno", sub: "Apoyo y propósito" },
  { id: "comments", title: "Tus Comentarios", sub: "Score global" },
  { id: "summary", title: "Confirmación", sub: "Resumen final" },
];

function getStepRules(step, anxAnswers, strAnswers, wbAnswers) {
  switch (step) {
    case 0: {
      const rules = {};
      for (let i = 0; i < 5; i++) {
        rules[`anx_${i}`] = {
          required: true,
          custom: () => anxAnswers[i] === "" ? "Selecciona una respuesta" : null,
        };
      }
      return rules;
    }
    case 1: {
      const rules = {};
      for (let i = 0; i < 5; i++) {
        rules[`str_${i}`] = {
          required: true,
          custom: () => strAnswers[i] === "" ? "Selecciona una respuesta" : null,
        };
      }
      return rules;
    }
    case 2: {
      const rules = {};
      for (let i = 0; i < 5; i++) {
        rules[`wb_${i}`] = {
          required: true,
          custom: () => wbAnswers[i] === "" ? "Selecciona una respuesta" : null,
        };
      }
      return rules;
    }
    case 3: return {}; // Comments are optional
    case 4: return {}; // Summary - no validation
    default: return {};
  }
}

export default function FormF5() {
  const { profile, setProfile, isDemo } = useUserProfile();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [fade, setFade] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { validateStep, getError, isStepValid, clearErrors, onFieldChange } = useFormValidation();

  const [anxAnswers, setAnxAnswers] = useState(Array(5).fill(""));
  const [strAnswers, setStrAnswers] = useState(Array(5).fill(""));
  const [wbAnswers, setWbAnswers] = useState(Array(5).fill(""));
  const [comentario1, setComentario1] = useState("");
  const [comentario2, setComentario2] = useState("");

  const rules = useMemo(() => getStepRules(step, anxAnswers, strAnswers, wbAnswers), [step, anxAnswers, strAnswers, wbAnswers]);

  const setAnxAnswer = useCallback((i, v) => {
    setAnxAnswers((p) => { const n = [...p]; n[i] = v; return n; });
    const rule = rules[`anx_${i}`];
    if (rule) onFieldChange(`anx_${i}`, v, rule);
  }, [rules, onFieldChange]);
  const setStrAnswer = useCallback((i, v) => {
    setStrAnswers((p) => { const n = [...p]; n[i] = v; return n; });
    const rule = rules[`str_${i}`];
    if (rule) onFieldChange(`str_${i}`, v, rule);
  }, [rules, onFieldChange]);
  const setWbAnswer = useCallback((i, v) => {
    setWbAnswers((p) => { const n = [...p]; n[i] = v; return n; });
    const rule = rules[`wb_${i}`];
    if (rule) onFieldChange(`wb_${i}`, v, rule);
  }, [rules, onFieldChange]);

  // Computed scores
  const allAnxAnswered = anxAnswers.every((a) => a !== "");
  const anxietyScore = allAnxAnswered ? sumScores(anxAnswers, ANXIETY_OPTIONS) : 0;

  const allStrAnswered = strAnswers.every((a) => a !== "");
  const stressScore = allStrAnswered ? sumScores(strAnswers, STRESS_OPTIONS) : 0;

  const allWbAnswered = wbAnswers.every((a) => a !== "");
  const wellbeingScore = allWbAnswered ? sumScores(wbAnswers, WELLBEING_OPTIONS) : 0;

  const globalScore = calculateGlobalScore(anxietyScore, stressScore, wellbeingScore);

  // Build flat data for validation
  const flatData = useMemo(() => {
    const d = {};
    anxAnswers.forEach((a, i) => { d[`anx_${i}`] = a; });
    strAnswers.forEach((a, i) => { d[`str_${i}`] = a; });
    wbAnswers.forEach((a, i) => { d[`wb_${i}`] = a; });
    return d;
  }, [anxAnswers, strAnswers, wbAnswers]);

  // Navigation
  const go = useCallback((dir) => {
    setFade(false);
    setTimeout(() => { setStep((s) => s + dir); setFade(true); }, 200);
  }, []);

  const goNext = useCallback(() => {
    if (isDemo) { go(1); return; }
    const errs = validateStep(rules, flatData);
    if (Object.keys(errs).length > 0) return;
    go(1);
  }, [step, rules, flatData, validateStep, go, isDemo]);

  const goBack = useCallback(() => { clearErrors(); go(-1); }, [go, clearErrors]);

  const nextDisabled = !isDemo && !isStepValid(rules, flatData);

  // Submit
  const handleSubmit = useCallback(async () => {
    if (isDemo) return;
    const now = new Date().toISOString();
    const anxScoreValues = anxAnswers.map((a) => ANXIETY_OPTIONS.indexOf(a));
    const strScoreValues = strAnswers.map((a) => STRESS_OPTIONS.indexOf(a));
    const wbScoreValues = wbAnswers.map((a) => WELLBEING_OPTIONS.indexOf(a));

    const f5Results = {
      ansiedad: { respuestas: anxScoreValues, score: anxietyScore, categoria: classifyAnxiety(anxietyScore).key },
      estres: { respuestas: strScoreValues, score: stressScore, categoria: classifyStress(stressScore).key },
      bienestar: { respuestas: wbScoreValues, score: wellbeingScore, categoria: classifyWellbeing(wellbeingScore).key },
      scoreGlobal: globalScore,
      categoriaGlobal: classifyGlobal(globalScore).key,
      comentario1,
      comentario2,
      createdAt: now,
      completedAt: now,
    };
    setSubmitted(true);
    await setProfile((prev) => ({
      ...prev,
      f5Results,
      fhirSyncStatus: "PENDING_FHIR",
      pendingFhirF5: true,
    }));

    const email = user?.email || profile.email;
    if (email) {
      syncF5(f5Results, email).then((res) => {
        if (res.ok) {
          console.log("[F5] Backend sync OK:", res.data);
          setProfile((prev) => ({
            ...prev,
            pendingFhirF5: false,
            fhirSyncStatus: "SYNCED",
            fhirSyncedAt: new Date().toISOString(),
          }));
        } else {
          console.warn("[F5] Backend sync failed (queued in Outbox):", res);
        }
      });
    }

    setTimeout(() => navigate("/dashboard"), 2000);
  }, [anxAnswers, strAnswers, wbAnswers, anxietyScore, stressScore, wellbeingScore, globalScore, comentario1, comentario2, setProfile, navigate, isDemo, user, profile.email]);

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-sans" style={{ background: "#FCFAEE" }}>
        <div className="we-card p-8 text-center max-w-[380px] w-full" style={{ boxShadow: "0 8px 30px rgba(45,42,36,0.10)" }} data-testid="f5-success-card">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "#F3E5F0" }}>
            <CheckCircle size={32} color="#883476" />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "#2D2A24" }}>¡Evaluación emocional completada!</h2>
          <p className="text-sm" style={{ color: "#71644C" }}>Redirigiendo a tu dashboard...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    const errProps = { errors: { getError } };
    switch (step) {
      case 0: return <Step1Anxiety answers={anxAnswers} onChange={setAnxAnswer} allAnswered={allAnxAnswered} score={anxietyScore} {...errProps} />;
      case 1: return <Step2Stress answers={strAnswers} onChange={setStrAnswer} allAnswered={allStrAnswered} score={stressScore} {...errProps} />;
      case 2: return <Step3Wellbeing answers={wbAnswers} onChange={setWbAnswer} allAnswered={allWbAnswered} score={wellbeingScore} {...errProps} />;
      case 3: return <Step4Comments comentario1={comentario1} onComentario1Change={setComentario1} comentario2={comentario2} onComentario2Change={setComentario2} globalScore={globalScore} />;
      case 4: return <Step5Summary anxietyScore={anxietyScore} stressScore={stressScore} wellbeingScore={wellbeingScore} globalScore={globalScore} />;
      default: return null;
    }
  };

  return (
    <DemoFormOverlay>
      <WizardLayout
        steps={STEPS}
        currentStep={step}
        onNext={goNext}
        onBack={goBack}
        fade={fade}
        userName={profile.nombres}
        isLastStep={step === 4}
        onSubmit={handleSubmit}
        submitDisabled={isDemo}
        nextDisabled={nextDisabled}
        nextLabel={step === 0 ? "Comenzar evaluación \u2192" : undefined}
        submitLabel="Enviar evaluación"
      >
        {renderStep()}
      </WizardLayout>
    </DemoFormOverlay>
  );
}
